import { ParameterCodeDataPolicy } from "constants/common/ParameterCodeDataPolicy";
import { ResponseEntity } from "domain/entity/Common/ResponseEntity";
import { DirectDataInputEntity, EMPTY_DIRECT_DATA_INPUT_ENTITY } from "domain/entity/DirectDataInput/DirectDataInputEntity";
import { DirectDataInputSearchCriteria, EMPTY_DIRECT_DATA_INPUT_SEARCH_CRITERIA } from "domain/entity/DirectDataInput/DirectDataInputSearchCriteria";
import { DirectDataInputRepository } from "domain/repository/DirectDataInput/DirectDataInputRepo";
import { ForwarderCodeRepository } from "domain/repository/ForwarderCode/ForwarderCodeRepo";
import { ParameterDetailRepository } from "domain/repository/parameter/ParameterDetailRepo";
import { TerminalRepository } from "domain/repository/Terminal/TerminalRepo";
import { createDirectDataInputValidationSchema, createSaptDirectDataInputValidationSchema } from "presentation/constant/DirectDataInput/DirectDataInputValidationSchema";
import { masterTypeDropdownOption } from "presentation/constant/DropDownOptions/StaticDropdownOptions";
import { Validation } from "presentation/constant/Validation";
import { DirectDataInputModel } from "presentation/model/DirectDataInput/DirectDataInputModel";
import { Dispatch, SetStateAction } from "react";
import { IFieldValue } from "veronica-ui-component/dist/component/core";
import BaseViewModel from "../BaseViewModel";

interface DirectDataInputVMProps extends BaseViewModel {
    dispatch: [
        Dispatch<SetStateAction<DirectDataInputModel>> | ((value: SetStateAction<DirectDataInputModel>) => void),
    ],
    directDataInputRepo: DirectDataInputRepository,
    parameterDetailRepo: ParameterDetailRepository,
    terminalRepo: TerminalRepository,
    forwarderCodeRepo: ForwarderCodeRepository,
}

export const DirectDataInputVM = ({ dispatch, directDataInputRepo, parameterDetailRepo, forwarderCodeRepo, terminalRepo }: DirectDataInputVMProps) => {
    const [directDataInputDispatch] = dispatch;

    const loadDropdownOption = async () => {

        await forwarderCodeRepo.getAllForwarderCodes().then(
            forwarderCodes => {
                const forwarderCodesDropdownOptions = forwarderCodes?.map((forwarderCode) => ({
                    dropdownLabel: forwarderCode.forwarderCode,
                    tagLabel: forwarderCode.forwarderCode,
                    value: forwarderCode.forwarderCode,
                })) ?? []

                directDataInputDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        forwarderCodeDropdownOptions: forwarderCodesDropdownOptions
                    }
                }))
            }
        )

        await terminalRepo.getTerminalsForComboBox().then(
            terminalList => {
                const terminalDropdownOptions = terminalList?.map((terminal) => ({
                    dropdownLabel: terminal.terminalCode,
                    tagLabel: terminal.terminalCode,
                    value: terminal.terminalCode,
                })) ?? []

                directDataInputDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        operatingTmlDropdownOptions: terminalDropdownOptions
                    }
                }))
            }
        )

        await parameterDetailRepo.getAllParameterDtlsByParameterCode(ParameterCodeDataPolicy.DAMAGE_TYPE).then(
            locationTypeList => {
                const damageTypeDropdownOption = locationTypeList?.map((locationType) => ({
                    dropdownLabel: locationType.parameterDtlCode,
                    tagLabel: locationType.parameterDtlCode,
                    value: locationType.parameterDtlCode,
                })) ?? []

                directDataInputDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        damageTypeDropdownOptions: damageTypeDropdownOption
                    }
                }))
            }
        )
    }

    const updateSelectedRows = async (selecedRows: DirectDataInputEntity[]) => {
        directDataInputDispatch(prevState => {

            return {
                ...prevState,
                selectedRows: selecedRows,
                forceRefresh: !prevState.forceRefresh
            }
        })
    }

    const onRowDoubleClick = async (row: DirectDataInputEntity) => {
        directDataInputDispatch(prevState => {

            return {
                ...prevState,
                isShowEditPanel: true,
                currentRow: row,
            }
        })
    }

    const onSearchClick = async () => {
        directDataInputDispatch(prevState => {

            return {
                ...prevState,
                isShowSearchPanel: !prevState.isShowSearchPanel,
            }
        })
    }

    const onEditClick = async () => {
        directDataInputDispatch(prevState => {

            return {
                ...prevState,
                isEdit: true,
                currentEditRow: prevState.currentRow,
            }
        })
    }

    const onResetClick = async (isAdd: boolean) => {
        directDataInputDispatch(prevState => {

            return {
                ...prevState,
                currentEditRow: isAdd ? { ...EMPTY_DIRECT_DATA_INPUT_ENTITY } : prevState.currentRow,
            }
        })
    }

    const onAddClick = async () => {
        directDataInputDispatch(prevState => {
            return {
                ...prevState,
                isAdd: true,
                isShowEditPanel: true,
                currentEditRow: {
                    ...EMPTY_DIRECT_DATA_INPUT_ENTITY,
                    status: 'NEW',
                    operatingTml: prevState.dynamicOptions.operatingTmlDropdownOptions.length > 0 ? prevState.dynamicOptions.operatingTmlDropdownOptions[0].value : '',
                    forwarderCode: prevState.dynamicOptions.forwarderCodeDropdownOptions.length > 0 ? prevState.dynamicOptions.forwarderCodeDropdownOptions[0].value : '',
                    masterType: masterTypeDropdownOption[0].value,
                },
            }
        })
    }

    const onCloseEidtPanel = async () => {
        directDataInputDispatch(prevState => {
            return {
                ...prevState,
                isAdd: false,
                isEdit: false,
                isShowEditPanel: false,
                selectedRows: [],
                forceRefresh: !prevState.forceRefresh,
                currentEditRow: { ...EMPTY_DIRECT_DATA_INPUT_ENTITY },
                masterState: {
                    ...prevState.masterState,
                    allFormState: {},
                }
            }
        })
    }

    const onSearchCriteriaResetClick = async () => {
        directDataInputDispatch(prevState => {
            return {
                ...prevState,
                searchCriteria: { ...EMPTY_DIRECT_DATA_INPUT_SEARCH_CRITERIA },
            }
        })
    }

    const onSaveClicked = () => {
        directDataInputDispatch(prevState => {
            return {
                ...prevState,
                masterState: {
                    ...prevState.masterState,
                    isSaveClicked: true,
                    allFormState: {},
                }
            }
        })
    }

    const onSave = async (data: DirectDataInputEntity, operatingUnit: string): Promise<ResponseEntity> => {
        const valResult = await Validation((operatingUnit === 'SAPT' || operatingUnit === 'KICT') ? createSaptDirectDataInputValidationSchema : createDirectDataInputValidationSchema).ValidateFormOnly(data);
        let validatedResult: { [x: string]: string } = {};
        if (valResult) {
            validatedResult = { ...valResult };

            directDataInputDispatch(prevState => {
                return {
                    ...prevState,
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {
                            ...validatedResult
                        },
                    }
                }
            });

            const res: ResponseEntity = {
                code: "",
                success: false,
                msg: null,
                data: null
            }

            return res;
        }
        return await directDataInputRepo.saveEntity(data);
    }

    const onDelete = async (ids: number[]): Promise<ResponseEntity> => {
        return await directDataInputRepo.deleteEntity(ids);
    }

    const onUnconfirm = async (ids: number[]): Promise<ResponseEntity> => {
        return await directDataInputRepo.unConfirmEntity(ids);
    }

    const onConfirm = async (ids: number[]): Promise<ResponseEntity> => {
        return await directDataInputRepo.confirmEntity(ids);
    }

    const onSearch = async (searchCriteria: DirectDataInputSearchCriteria) => {
        directDataInputDispatch(prevState => {
            return {
                ...prevState,
                // directDataInputList: [],
                searchCriteria: searchCriteria,
                isAdd: false,
                isEdit: false,
                isShowEditPanel: false,
                selectedRows: [],
                forceRefresh: !prevState.forceRefresh,
            }
        })
        await directDataInputRepo.getEntities(searchCriteria).then((data) => {
            directDataInputDispatch(prevState => {
                return {
                    ...prevState,
                    directDataInputList: data,
                }
            })
        }).catch((error) => {
            return [];
        })
    }

    const onSearchFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;

        directDataInputDispatch(prevState => {
            return {
                ...prevState,
                searchCriteria: {
                    ...prevState.searchCriteria,
                    [fieldKey]: val,
                },
            }
        })
    }

    const onDateRangeChange = (startDate: any, endDate: any, dateFields: { startField: string, endField: string }) => {
        directDataInputDispatch(prevState => ({
            ...prevState,
            searchCriteria: {
                ...prevState.searchCriteria,
                [dateFields.startField]: startDate,
                [dateFields.endField]: endDate,
            }
        }))
    }

    const onFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        directDataInputDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: {
                    ...prevState.currentEditRow,
                    [fieldKey]: val,
                },
                masterState: {
                    ...prevState.masterState,
                    allFormState: {
                        ...prevState.masterState.allFormState,
                        [fieldKey]: ''
                    },
                }
            }
        })
    }

    const setIsSearchLoading = (isSearchLoading: boolean) => {
        directDataInputDispatch(prevState => {
            return {
                ...prevState,
                isSearchLoading: isSearchLoading,
            }
        })
    }

    return {
        onFieldChange: onFieldChange,
        onCloseEidtPanel: onCloseEidtPanel,
        onConfirm: onConfirm,
        onUnconfirm: onUnconfirm,
        onDateRangeChange: onDateRangeChange,
        onSearchFieldChange: onSearchFieldChange,
        onSearchCriteriaResetClick: onSearchCriteriaResetClick,
        onSearch: onSearch,
        onDelete: onDelete,
        onSave: onSave,
        onSearchClick: onSearchClick,
        onAddClick: onAddClick,
        onEditClick: onEditClick,
        onResetClick: onResetClick,
        onRowDoubleClick: onRowDoubleClick,
        updateSelectedRows: updateSelectedRows,
        loadDropdownOption: loadDropdownOption,
        onSaveClicked: onSaveClicked,
        setIsSearchLoading: setIsSearchLoading,
    }
}